import * as CryptoJS from 'crypto-js';
import { ProductSet } from '../..';
import config from '../../config';
import { Api, ApiConfig } from '../api';

// 定义 createSpace 的请求参数类型
export interface CreateSpaceRequest {
    title: string;
    description: string;
    userShopId: string;
}

// 定义 createSpace 的响应类型
export interface CreateSpaceResponse {
    success: boolean;
    space_id: string;
}

// 定义 updateSpace 的请求参数类型
export interface UpdateSpaceRequest {
    title?: string;
    description?: string;
    product_url?: string | null;
}

// 定义 updateSpace 的响应类型
export interface UpdateSpaceResponse {
    success: boolean;
    space_id: string;
}

// 定义 getUserSpaces 的响应类型
export interface UserSpace {
    id: string;
    created_at: string;
}

export interface GetUserSpacesResponse extends Array<UserSpace> {}

export interface GetOrdersRequest {
    /**
     * 支付状态，可选值：pending、authorized、paid、refunded等；
     * 非必填，默认查询所有支付状态。
     */
    financialStatus?: string;
    /**
     * 履约状态，可选值：unfulfilled、fulfilled、partial、restocked；
     * 非必填，默认查询所有履约状态。
     */
    fulfillmentStatus?: string;
    /**
     * 分页；
     * 非必填，默认为1
     */
    page?: string;
    /**
     * 分页数；
     * 非必填，默认为20.
     */
    size?: string;
    /**
     * user_space表的主键ID，区分归属在哪个space下的订单。
     * 非必填，默认查询所有。
     */
    spaceId?: string;
    [property: string]: any;
}


export interface GetOrdersResponse {
    data: Data;
    success: boolean;
    [property: string]: any;
}

export interface Data {
    orders: Order[];
    pagination: Pagination;
    [property: string]: any;
}

export interface Order {
    carrierName?: null;
    costPrice?: null;
    createdAt?: string;
    currency?: string;
    customer?: Customer;
    financialStatus?: string;
    fulfillmentStatus?: string;
    lastWebhookReceivedAt?: string;
    lineItems?: LineItems;
    merchantDueAmount?: null;
    orderNo?: string;
    shippingAddress?: ShippingAddress;
    shippingCost?: null;
    shopId?: string;
    shopifyOrderId?: number;
    spaceId?: string;
    totalPrice?: number;
    trackingNumber?: null;
    transactionId?: null;
    updatedAt?: string;
    userId?: string;
    webhookEvent?: string;
    [property: string]: any;
}

export interface Customer {
    admin_graphql_api_id: string;
    created_at: string;
    currency: string;
    default_address: DefaultAddress;
    email: string;
    first_name: string;
    id: number;
    last_name: string;
    multipass_identifier: null;
    note: null;
    phone: null;
    state: string;
    tax_exempt: boolean;
    tax_exemptions: string[];
    updated_at: string;
    verified_email: boolean;
    [property: string]: any;
}

export interface DefaultAddress {
    address1: string;
    address2: string;
    city: string;
    company: null;
    country: string;
    country_code: string;
    country_name: string;
    customer_id: number;
    default: boolean;
    first_name: string;
    id: number;
    last_name: string;
    name: string;
    phone: null;
    province: string;
    province_code: string;
    zip: string;
    [property: string]: any;
}

export interface LineItems {
    admin_graphql_api_id: string;
    attributed_staffs: string[];
    current_quantity: number;
    discount_allocations: string[];
    duties: string[];
    fulfillable_quantity: number;
    fulfillment_service: string;
    fulfillment_status: null;
    gift_card: boolean;
    grams: number;
    id: number;
    name: string;
    price: string;
    price_set: PriceSet;
    product_exists: boolean;
    product_id: number;
    properties: string[];
    quantity: number;
    requires_shipping: boolean;
    sales_line_item_group_id: null;
    sku: string;
    tax_lines: string[];
    taxable: boolean;
    title: string;
    total_discount: string;
    total_discount_set: TotalDiscountSet;
    variant_id: number;
    variant_inventory_management: null;
    variant_title: string;
    vendor: string;
    [property: string]: any;
}

export interface PriceSet {
    presentment_money: PriceSetPresentmentMoney;
    shop_money: PriceSetShopMoney;
    [property: string]: any;
}

export interface PriceSetPresentmentMoney {
    amount: string;
    currency_code: string;
    [property: string]: any;
}

export interface PriceSetShopMoney {
    amount: string;
    currency_code: string;
    [property: string]: any;
}

export interface TotalDiscountSet {
    presentment_money: TotalDiscountSetPresentmentMoney;
    shop_money: TotalDiscountSetShopMoney;
    [property: string]: any;
}

export interface TotalDiscountSetPresentmentMoney {
    amount: string;
    currency_code: string;
    [property: string]: any;
}

export interface TotalDiscountSetShopMoney {
    amount: string;
    currency_code: string;
    [property: string]: any;
}

export interface ShippingAddress {
    address1: string;
    address2: string;
    city: string;
    company: null;
    country: string;
    country_code: string;
    first_name: string;
    last_name: string;
    latitude: number;
    longitude: number;
    name: string;
    phone: null;
    province: string;
    province_code: string;
    zip: string;
    [property: string]: any;
}

export interface Pagination {
    hasNext: boolean;
    hasPrevious: boolean;
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    [property: string]: any;
}


// 定义 getUserSpaceDetail 的响应类型
export interface UserSpaceDetail {
    id: string;
    user_id: string;
    title: string;
    description: string;
    product_url: string | null;
    created_at: string;
    user_shop_id: string;
    preview_data: ProductSet;
    template_address: string;
    source_product_id: string;
}

export interface GetUserSpacesBatchRequest {
    /**
     * 分页页码
     * 非必填，默认为1
     */
    page?: number;
    /**
     * 分页大小
     * 非必填，默认为20
     */
    size?: number;
}

export interface GetUserSpacesBatchResponse {
    spaces: UserSpaceDetail[];
    pagination: Pagination;
}

export interface GetPulseReportsRequest {
    /**
     * Start time for the report query
     * @example "2025-09-28"
     */
    start_time?: string;
    /**
     * End time for the report query  
     * @example "2025-09-29"
     */
    end_time?: string;
    /**
     * Page number for pagination
     * @example 1
     */
    page?: number;
    /**
     * Page size for pagination
     * @example 10
     */
    size?: number;
}

export interface PulseReport {
    id: string;
    user_id: string;
    report_date: string;
    report_type: string;
    title: string;
    brief: string;
    img_url: string | null;
    oss_url: string | null;
    status: string;
    created_at: string;
    updated_at: string;
}

export interface GetPulseReportsResponse {
    data: PulseReport[];
    page: number;
    size: number;
    total: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
}


export interface UpdateOnboardingRequest {
    [key: string]: any;
}

export interface UpdateOnboardingResponse {
    success: boolean;
    message?: string;
}

export interface CreateOnboardingRequest {
    /**
     * User name
     */
    user_name: string;
    /**
     * Domain name
     */
    domain_name: string;
    /**
     * Brand name
     */
    brand_name: string;
    /**
     * Platform type
     */
    platform: string;
    /**
     * Timezone
     */
    timezone: string;
}

export interface OnboardingData {
    shop_id: string;
    pulse_id: number;
    report_utc_hour: number;
}

export interface CreateOnboardingResponse {
    success: boolean;
    message: string;
    data: OnboardingData;
}



export interface DeleteSpaceResponse {
    success: boolean;
    message?: string;
}

export interface SendUsernameRequest {
    user_name: string;
}

export interface SendUsernameResponse {
    success: boolean;
    message?: string;
}

export interface SendGenericMessageRequest {
    title: string;
    content: Record<string, string>;
}

export interface SendGenericMessageResponse {
    success: boolean;
    message?: string;
}

export class FeatureService {
    private api: Api;

    constructor(config: ApiConfig) {
        this.api = new Api(config);
    }

    /**
     * Create a new space
     * @param data - Space creation data
     * @returns Promise with space creation result
     */
    async createSpace(data: CreateSpaceRequest): Promise<CreateSpaceResponse> {
        return this.api.post<CreateSpaceResponse>('/api/shopify/createSpace', data);
    }

    /**
     * Get user spaces list
     * @param userShopId - User shop ID
     * @returns Promise with user spaces list
     */
    async getUserSpaces(userShopId: string): Promise<GetUserSpacesResponse> {
        return this.api.get<GetUserSpacesResponse>(`/api/shopify/userSpaces/${userShopId}`);
    }

    /**
     * Get specific space detail
     * @param spaceId - Space ID
     * @returns Promise with space detail
     */
    async getUserSpaceDetail(spaceId: string): Promise<UserSpaceDetail> {
        return this.api.get<UserSpaceDetail>(`/api/shopify/userSpaceDetail/${spaceId}`);
    }

    /**
     * Update space information
     * @param spaceId - Space ID
     * @param data - Space update data
     * @returns Promise with update result
     */
    async updateSpace(spaceId: string, data: UpdateSpaceRequest): Promise<UpdateSpaceResponse> {
        return this.api.put<UpdateSpaceResponse>(`/api/shopify/updateSpace/${spaceId}`, data);
    }

    /**
     * Delete space (logical deletion)
     * @param spaceId - Space ID to delete
     * @returns Promise with deletion result
     */
    async deleteSpace(spaceId: string): Promise<DeleteSpaceResponse> {
        return this.api.delete<DeleteSpaceResponse>(`/api/shopify/deleteSpace/${spaceId}`);
    }

    /**
     * Get current user's order information
     * @param params - Query parameters for filtering orders
     * @returns Promise with user orders data
     */
    async getUserOrders(params?: GetOrdersRequest): Promise<GetOrdersResponse> {
        return this.api.get<GetOrdersResponse>('/api/orders/getUserOrders', { params });
    }

    /**
     * Batch query user's space detailed information
     * @param params - Query parameters for pagination
     * @returns Promise with user spaces batch data
     */
    async getUserSpacesBatch(params?: GetUserSpacesBatchRequest): Promise<GetUserSpacesBatchResponse> {
        return this.api.get<GetUserSpacesBatchResponse>('/api/shopify/userSpacesBatch', { params });
    }

    /**
     * Get pulse reports list
     * @param params - Query parameters for filtering and pagination
     * @returns Promise with pulse reports data
     */
    async getPulseReports(params?: GetPulseReportsRequest): Promise<GetPulseReportsResponse> {
        return this.api.get<GetPulseReportsResponse>('/api/pulse/reports', { params });
    }

    /**
     * Update onboarding data
     * @param shopInfoId - Shop info ID for the update
     * @param data - Update data object
     * @returns Promise with update response
     */
    async updateOnboarding(shopInfoId: string, data: UpdateOnboardingRequest): Promise<UpdateOnboardingResponse> {
        return this.api.put<UpdateOnboardingResponse>(`/api/pulse/update/${shopInfoId}`, data);
    }

    /**
     * @deprecated
     * Create onboarding data
     * @param data - Onboarding creation data
     * @returns Promise with creation response
     */
    async createOnboarding(data: CreateOnboardingRequest): Promise<CreateOnboardingResponse> {
        return this.api.post<CreateOnboardingResponse>('/api/pulse/insert', data);
    }

    /**
     * Get pulse report detail by ID
     * @param reportId - Report ID to fetch details for
     * @returns Promise with report detail data
     */
    async getPulseReportDetail(reportId: string): Promise<PulseReport> {
        return this.api.get<PulseReport>(`/api/pulse/report/${reportId}`);
    }

    async sendUsername(data: SendUsernameRequest): Promise<SendUsernameResponse> {
        return this.api.post<SendUsernameResponse>('/api/pulse/send-username', data);
    }

    async sendGenericMessage(data: SendGenericMessageRequest): Promise<SendGenericMessageResponse> {
        // 1. 准备数据
        const secret = "super_strong_internal_secret_key";
        const requestData = {
            title: data.title,
            content: data.content
        };

        // 必须保证 JSON 序列化后的字符串与后端收到的一致
        const requestBody = JSON.stringify(requestData);

        // 获取当前时间戳（秒）
        const timestamp = Math.floor(Date.now() / 1000);

        // 2. 生成签名
        // 签名规则: HmacSHA256(timestamp + ":" + requestBody, secret)
        const messageToSign = `${timestamp}:${requestBody}`;
        const signature = CryptoJS.HmacSHA256(messageToSign, secret).toString(CryptoJS.enc.Hex);

        // 3. 发送请求，添加自定义 headers
        return this.api.post<SendGenericMessageResponse>('/api/slack/sendPublicMessage', requestData, {
            headers: {
                'X-Timestamp': timestamp.toString(),
                'Authorization': `Bearer ${signature}`
            }
        });
    }
}

export const featureService = new FeatureService({
    baseURL: config.services.feature.apiHost,
    headers: {'Content-Type': 'application/json'}
});
